import type {
  CrmsAttachment,
  OverallSearchQuery,
  OverallSearchResultCategory,
  PageDestination,
  UploadedFileStatusType,
} from '@core/core/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import type { ProjectId } from 'common/types'

import {
  getAllCrmsImportProjectsApi,
  getKsuAllCompaniesApi,
  getProjectAttachmentsApi,
  getWellContainersByCompanyIdApi,
  searchKsuWellsApi,
  searchKsuWellsGroupsApi,
} from '../../api'
import type { AppDispatch, AppThunk, RootState } from '../store'
import {
  selectCategoryPage,
  selectSearchActiveCategory,
  selectSearchQuery,
} from './overallSearchSelectors'
import { overallSearchSlice } from './overallSearchSlice'

const {
  updateSearchQuery,
  setActiveCategoryId,
  setKsuCompanyEntities,
  updateKsuWellsState,
  clearSearchResults,
  setSearchResults,
  setSearchInCategoryResults,
  updateKsuCompanyEntity,
  initCategoryPagination,
  setCategoryPage,
} = overallSearchSlice.actions

export const searchFromDbSources = createAsyncThunk<
  Promise<void>,
  { query: OverallSearchQuery },
  { state: RootState; dispatch: AppDispatch }
>('OverallSearch', async ({ query: searchQuery }, thunkAPI) => {
  const dispatch = thunkAPI.dispatch
  try {
    dispatch(updateSearchQuery(searchQuery))

    const state = thunkAPI.getState()
    const query = selectSearchQuery(state)

    const promises: Promise<OverallSearchResultCategory[]>[] = []

    if (query.mode === 'alldb' || query.mode === 'crms') {
      promises.push(
        getAllCrmsImportProjectsApi(query.globalSearch ?? '', query.crms ?? {}).then(results =>
          results.map(value => ({
            source: 'crms',
            id: value.id,
            name: value.projectName,
            data: value,
          })),
        ),
      )
    }

    if (query.mode === 'alldb' || query.mode === 'geodb') {
      promises.push(
        searchKsuWellsGroupsApi(
          query.globalSearch ?? '',
          query.geodb?.company,
          query.geodb?.licenseArea,
          query.geodb?.field,
        ).then(results =>
          results.map(value => ({
            source: 'geodb',
            id: value.id,
            name: value.name,
            count: value.wellsCount,
            data: value,
          })),
        ),
      )
    }

    const apiResults = await Promise.allSettled(promises)

    const results = apiResults.flatMap(result =>
      result.status === 'fulfilled' ? result.value : [],
    )

    dispatch(setActiveCategoryId())
    dispatch(setSearchInCategoryResults())
    dispatch(setSearchResults(results))
  } catch (error) {
    dispatch(clearSearchResults())
  }
})

export const searchInCategory = createAsyncThunk<
  Promise<void>,
  { projectId: ProjectId; activeCategory: OverallSearchResultCategory | undefined },
  { state: RootState; dispatch: AppDispatch }
>('SearchInCategory', async ({ projectId, activeCategory }, thunkAPI) => {
  const dispatch = thunkAPI.dispatch
  dispatch(setActiveCategoryId(activeCategory?.id))

  if (activeCategory === undefined) return

  // Подгружаются скважины выбранного месторождения (или ЛУ) ГеоБД
  if (activeCategory.source === 'geodb') {
    dispatch(initCategoryPagination(activeCategory.data.wellsCount))

    const state = thunkAPI.getState()
    const query = selectSearchQuery(state)
    const page = selectCategoryPage(state)

    const wellsGroup = activeCategory.data
    const wells = await searchKsuWellsApi(
      projectId,
      wellsGroup,
      query.globalSearch ?? '',
      query.geodb?.company,
      query.geodb?.licenseArea,
      query.geodb?.field,
      page?.rows,
      page?.firstRow,
    )
    dispatch(setSearchInCategoryResults({ source: 'geodb', wells }))
  }

  // Подгружаются вложения выбранного проекта Црмс
  if (activeCategory.source === 'crms') {
    // По Црмс нет пагинации пока
    dispatch(initCategoryPagination(undefined))

    const crmsProject = activeCategory.data

    const attachments = await getProjectAttachmentsApi(projectId, crmsProject.id)
    const accumulator: CrmsAttachment = {
      id: '',
      contentFileName: '',
      path: '',
      type: 'folder',
      files: [],
      state: 'notstarted',
      contentBucket: '',
      contentId: '',
    }
    attachments.forEach(attachment => {
      parseCrmsAttachments(accumulator, attachment, attachment.path.split('/'))
    })
    dispatch(setSearchInCategoryResults({ source: 'crms', attachments: accumulator.files ?? [] }))
  }
})

export const gotoPageInCategory = createAsyncThunk<
  Promise<void>,
  { projectId: ProjectId; page: PageDestination },
  { state: RootState; dispatch: AppDispatch }
>('GotoPageInCategory', async ({ projectId, page }, thunkAPI) => {
  const dispatch = thunkAPI.dispatch
  dispatch(setCategoryPage(page))

  const state = thunkAPI.getState()
  const activeCategory = selectSearchActiveCategory(state)

  // Подгружаются скважины выбранного месторождения (или ЛУ) ГеоБД
  if (activeCategory?.source === 'geodb') {
    const query = selectSearchQuery(state)
    const page = selectCategoryPage(state)

    const wellsGroup = activeCategory.data
    const wells = await searchKsuWellsApi(
      projectId,
      wellsGroup,
      query.globalSearch ?? '',
      query.geodb?.company,
      query.geodb?.licenseArea,
      query.geodb?.field,
      page?.rows,
      page?.firstRow,
    )
    dispatch(setSearchInCategoryResults({ source: 'geodb', wells }))
  }
})

export const getAllAvailableCompanies = (): AppThunk => async dispatch => {
  const companies = await getKsuAllCompaniesApi()
  dispatch(setKsuCompanyEntities(companies))
}

export const updateGeoDbCompanyLists = (companyId: string) => async (dispatch: AppDispatch) => {
  const wellContaners = await getWellContainersByCompanyIdApi(companyId)
  dispatch(
    updateKsuCompanyEntity({
      companyId,
      fields: wellContaners.fields,
      licenseAreas: wellContaners.licenseAreas,
    }),
  )
}

const parseCrmsAttachments = (
  parent: CrmsAttachment,
  attachment: CrmsAttachment,
  paths: string[],
) => {
  if (paths.length === 1) {
    parent.files?.push(attachment)
    if (attachment.type === 'file') {
      parent.contentSize = (parent.contentSize ?? 0) + (attachment.contentSize ?? 0)
    }
    return
  }

  const path = paths.shift()
  const child = parent.files?.find(file => file.contentFileName === path)
  if (child) {
    parseCrmsAttachments(child, attachment, paths)
    child.contentSize = child.files?.reduce(
      (parentSumSize, child) => parentSumSize + (child.contentSize ?? 0),
      0,
    )
    return
  }

  const folder: CrmsAttachment = {
    id: parent.id + path,
    contentFileName: path ?? '',
    contentSize: 0,
    path: path ?? '',
    type: 'folder',
    files: [],
    state: parent.state,
    contentBucket: parent.contentBucket,
    contentId: parent.contentId,
  }
  parent.contentSize = (parent.contentSize ?? 0) + (attachment.contentSize ?? 0)
  parent.files?.push(folder)

  parseCrmsAttachments(folder, attachment, paths)
  folder.contentSize = folder.files?.reduce(
    (parentSumSize, child) => parentSumSize + (child.contentSize ?? 0),
    0,
  )
}

export const updateGeoDbFoundEntities =
  (entityIds: string[], state: UploadedFileStatusType): AppThunk =>
  dispatch => {
    dispatch(updateKsuWellsState({ wellIds: entityIds, state }))
  }
